import type { CSSProperties } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/Notification';

import { useTriggerAllMessagesMutation } from '@/services/triggerDfpMessageApi';
import type { DataMockFileMeta, ScheduleSummary, VehicleConfig } from '../../types';
import { formatCountdown, getNextFireTime, useCountdown } from '../../utils/useCountdown';
import VehicleDetailPanel from '../VehicleDetail/VehicleDetailPanel';

interface VehicleCardProps {
    vehicle: VehicleConfig;
    isExpanded: boolean;
    isNextOverall: boolean;
    onToggleExpand: () => void;
    onViewMessageConfig: (schedule: ScheduleSummary) => void;
    onOpenSourceFile: (file: DataMockFileMeta) => void;
    onOpenInArbitrary: (schedule: ScheduleSummary, mergedJson: string, overriddenJsonPaths: string[]) => void;
}

const vehicleTypeIcon = (assetType: string): string => {
    switch (assetType) {
        case 'car':
            return 'icon-car text-color-success';
        case 'bus':
            return 'icon-bus text-color-info';
        case 'van':
            return 'icon-van text-color-warning';
        case 'trailer':
            return 'icon-trailer text-color-danger';
        case 'truck':
        default:
            return 'icon-truck text-color-primary';
    }
};

// Color-code the telematics box type with explicit colors that don't clash
// with existing meanings (green = active, orange = imminent cron). CM4 is the
// newer generation (blue), TBM3 is the older one (brown).
const deviceTypeBadgeStyle = (deviceType: string): CSSProperties => {
    switch (deviceType.toUpperCase()) {
        case 'CM4':
            return { backgroundColor: '#aac6e3', color: '#1f3a5a' };
        case 'TBM3':
            return { backgroundColor: '#d8b48f', color: '#4d3014' };
        default:
            return { backgroundColor: '#c3ccd2', color: '#3a444b' };
    }
};

const VehicleCard = ({
    vehicle,
    isExpanded,
    isNextOverall,
    onToggleExpand,
    onViewMessageConfig,
    onOpenSourceFile,
    onOpenInArbitrary,
}: VehicleCardProps) => {
    const intl = useIntl();
    const independentCount = vehicle.data_mock_files.filter((f) => f.type === 'independent').length;
    const dependentCount = vehicle.data_mock_files.filter((f) => f.type === 'dependent').length;

    const [triggerAll, { isLoading: isTriggeringAll }] = useTriggerAllMessagesMutation();

    const nearestSchedule =
        vehicle.schedules
            .map((s) => ({ schedule: s, nextFire: getNextFireTime(s.cron) }))
            .filter((x): x is { schedule: typeof x.schedule; nextFire: number } => x.nextFire !== null)
            .sort((a, b) => a.nextFire - b.nextFire)[0] ?? null;

    const countdown = useCountdown(nearestSchedule?.schedule.cron ?? '');

    return (
        <div className="panel panel-default margin-bottom-15">
            <div className={`panel-body ${isExpanded ? 'bg-highlight-decent' : ''}`}>
                <div className="display-flex align-items-center justify-content-between">
                    {/* Left: Icon + Name */}
                    <div className="display-flex align-items-center gap-15">
                        <div
                            className="width-50 height-50 bg-lightest rounded display-flex align-items-center
                            justify-content-center border border-color-light flex-0-0-auto position-relative
                            overflow-hidden"
                        >
                            <span className={`uikit-icon text-size-h1 ${vehicleTypeIcon(vehicle.asset_type)}`} />
                            <span
                                className="position-absolute bg-darker text-color-white text-size-12 text-bold
                                display-flex align-items-center justify-content-center"
                                style={{ width: 18, height: 18, bottom: 0, right: 0, borderTopLeftRadius: 6 }}
                            >
                                {vehicle.service_care_booking?.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div>
                            <div className="display-flex align-items-center gap-5">
                                <span className="text-size-16 text-bold text-uppercase">{vehicle.asset_name}</span>
                            </div>
                            <div className="margin-top-3 display-flex align-items-center gap-5">
                                <span className="label label-default font-family-mono text-size-11">
                                    VIN: {vehicle.vin}
                                </span>
                                {vehicle.device_type && (
                                    <span
                                        className="label text-size-11 text-uppercase"
                                        style={deviceTypeBadgeStyle(vehicle.device_type)}
                                    >
                                        {vehicle.device_type}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Stats + Actions */}
                    <div className="display-flex align-items-center gap-15">
                        {/* Messages */}
                        <div className="text-center padding-x-10">
                            <div className="text-size-10 text-uppercase text-color-gray text-bold margin-bottom-3">
                                <FormattedMessage id="intl-msg:vehicleCard.messages" />
                            </div>
                            <div className="display-flex align-items-center gap-3 justify-content-center">
                                <span className="uikit-icon icon-layer text-size-12 text-color-gray" />
                                <span className="text-bold text-size-14">{vehicle.schedules.length}</span>
                            </div>
                        </div>

                        {/* Data Mocks */}
                        <div className="text-center padding-x-10 border-left border-right border-color-lighter">
                            <div className="text-size-10 text-uppercase text-color-primary text-bold margin-bottom-5">
                                <FormattedMessage id="intl-msg:vehicleCard.dataMocks" />
                            </div>
                            <div className="display-flex gap-10 justify-content-center">
                                <div className="text-center">
                                    <div className="text-size-9 text-color-gray text-uppercase margin-bottom-2">
                                        <FormattedMessage id="intl-msg:vehicleCard.independent" />
                                    </div>
                                    <span className="label label-primary label-condensed">{independentCount}</span>
                                </div>
                                <div className="text-center">
                                    <div className="text-size-9 text-color-gray text-uppercase margin-bottom-2">
                                        <FormattedMessage id="intl-msg:vehicleCard.dependent" />
                                    </div>
                                    <span className="label label-info label-condensed">{dependentCount}</span>
                                </div>
                            </div>
                        </div>

                        {/* Next Cron */}
                        {nearestSchedule !== null && (
                            <div className="text-center padding-x-10" style={{ minWidth: 120, maxWidth: 140 }}>
                                <div className="text-size-10 text-uppercase text-color-gray text-bold margin-bottom-3">
                                    <FormattedMessage id="intl-msg:vehicleCard.nextCron" />
                                </div>
                                <span
                                    className={`label font-family-mono 
                                    ${isNextOverall ? 'label-warning' : 'label-default'}`}
                                >
                                    {formatCountdown(countdown)}
                                </span>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="display-flex gap-5 margin-left-5">
                            <Button
                                disabled={isTriggeringAll}
                                style={{ minWidth: 120 }}
                                onClick={async () => {
                                    try {
                                        await triggerAll({ vin: vehicle.vin }).unwrap();
                                        Notification.success(`Triggered all ${vehicle.schedules.length} messages`);
                                    } catch (err: unknown) {
                                        const status = (err as { status?: number })?.status;
                                        const msg =
                                            status === 403
                                                ? intl.formatMessage({
                                                      id: 'intl-msg:common.error.insufficientPermissions',
                                                  })
                                                : intl.formatMessage({ id: 'intl-msg:common.error.failedToTrigger' });
                                        Notification.error(msg);
                                    }
                                }}
                            >
                                <span className="uikit-icon icon-play margin-right-5" />
                                {isTriggeringAll
                                    ? intl.formatMessage({ id: 'intl-msg:common.sending' })
                                    : intl.formatMessage({ id: 'intl-msg:vehicleCard.triggerAll' })}
                            </Button>
                            <Button bsStyle={isExpanded ? 'primary' : 'default'} iconOnly onClick={onToggleExpand}>
                                <span
                                    className={`uikit-icon ${isExpanded ? 'icon-chevron-up' : 'icon-chevron-down'}`}
                                />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {isExpanded && (
                <VehicleDetailPanel
                    vehicle={vehicle}
                    onViewMessageConfig={onViewMessageConfig}
                    onOpenSourceFile={onOpenSourceFile}
                    onOpenInArbitrary={onOpenInArbitrary}
                />
            )}
        </div>
    );
};

export default VehicleCard;
